import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEventHistory } from "../api/events-api";
import {
  FINANCE_QUERY_KEY,
  useDeleteFinanceTransaction,
  useGetFinanceTransaction,
  useUpdateFinanceTransaction,
} from "../api/finance-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import { Icon } from "../components/dss/Icon";
import {
  emptyTransactionForm,
  toPayload,
  TransactionForm,
  transactionToForm,
  TransactionFormValues,
  VaultHeader,
} from "../components/dss/finance/TransactionForm";
import { VaultSkeleton } from "../components/dss/finance/VaultSkeleton";
import { formatKr } from "../finance/ledger";

const EditTransaction = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const { data: transaction, isLoading } = useGetFinanceTransaction(transactionId);
  const { data: events = [] } = useEventHistory();
  const { data: members = [] } = useLogeMembers();
  const { mutateAsync: updateTransaction, isLoading: isSaving } = useUpdateFinanceTransaction();
  const { mutateAsync: deleteTransaction, isLoading: isDeleting } = useDeleteFinanceTransaction();

  const [values, setValues] = useState<TransactionFormValues>(emptyTransactionForm);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // The row arrives after the first render, so the form is seeded from it
  // rather than initialised with it.
  useEffect(() => {
    if (transaction) setValues(transactionToForm(transaction));
  }, [transaction]);

  const payload = toPayload(values);
  const income = values.direction === 1;

  const done = () => {
    queryClient.invalidateQueries(FINANCE_QUERY_KEY);
    queryClient.invalidateQueries(["finance_transaction", transactionId]);
    navigate("/finance");
  };

  const onSave = async () => {
    if (!payload || !transaction) return;
    await updateTransaction({ id: transaction.id, payload });
    done();
  };

  const onDelete = async () => {
    if (!transaction) return;
    await deleteTransaction(transaction.id);
    done();
  };

  return (
    <div className="mx-auto max-w-md dss-vault text-vault-ink pt-safe h-app flex flex-col overflow-hidden">
      <VaultHeader
        back="Finans"
        onBack={() => navigate("/finance")}
        title={
          <>
            Ret postering<span className={income ? "text-mint" : "text-ember"}>.</span>
          </>
        }
        sub={transaction ? `BOGFØRT ${transaction.occurred_on}` : undefined}
        action={
          transaction && (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-ember flex items-center gap-1.5"
            >
              <Icon name="trash-can" size={12} />
              Slet
            </button>
          )
        }
      />

      <div className="px-7 pt-6 flex-1 overflow-y-auto min-h-0 pb-4">
        {isLoading ? (
          <div className="flex flex-col gap-6">
            <VaultSkeleton className="h-10" />
            <VaultSkeleton className="h-16" />
            <VaultSkeleton className="h-24" />
          </div>
        ) : !transaction ? (
          <div className="font-mono text-[11px] tracking-wide uppercase text-vault-ink3">
            Posteringen findes ikke længere.
          </div>
        ) : confirmingDelete ? (
          <div className="dss-vault-card rounded-lg p-5">
            <div className="font-display text-[17px] font-bold uppercase tracking-tight">
              Slet posteringen?
            </div>
            <div className="font-mono text-[10.5px] tracking-wide text-vault-ink3 mt-2 uppercase">
              Saldoen genberegnes uden den. Rækken gemmes, men vises ikke igen.
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirmingDelete(false)}
                className="flex-1 py-3 rounded-lg border border-white/15 font-display text-[13px] font-bold uppercase tracking-tight"
              >
                Behold
              </button>
              <button
                onClick={onDelete}
                disabled={isDeleting}
                className="flex-1 py-3 rounded-lg bg-ember text-vault font-display text-[13px] font-bold uppercase tracking-tight disabled:opacity-40"
              >
                Slet
              </button>
            </div>
          </div>
        ) : (
          <TransactionForm
            values={values}
            onChange={setValues}
            events={events}
            members={members}
          />
        )}
      </div>

      {transaction && !confirmingDelete && (
        <div className="px-7 pb-5 pt-3">
          <button
            disabled={!payload || isSaving}
            onClick={onSave}
            className={`w-full py-3.5 px-4 rounded-lg font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3 disabled:opacity-40 ${
              income ? "bg-mint text-vault" : "bg-ember text-vault"
            }`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Icon name="check" size={14} />
              <span className="truncate">Gem ændringer</span>
            </span>
            <span className="font-mono text-[11px] opacity-75 flex items-center gap-1.5 flex-shrink-0">
              {payload ? formatKr(payload.amountOre, { signed: true, prefix: false }) : "0,00"}
              <Icon name="arrow-right" size={13} />
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default EditTransaction;
